import { SEO } from './SEO';
import type { Imovel } from '@/integrations/supabase/types';
import { getFotoUrl, getFotosUrls } from '@/utils/fotoHelpers';

interface PropertySEOProps {
  property: Imovel;
  siteName?: string;
}

/**
 * Componente SEO específico para páginas de imóveis
 * Gera meta tags otimizadas e Schema.org markup
 */
export function PropertySEO({ property, siteName = 'Imobiliária' }: PropertySEOProps) {
  // Construir título otimizado
  const propertyType = property.tipo || 'Imóvel';
  const location = [property.endereco_bairro, property.endereco_cidade, property.endereco_uf]
    .filter(Boolean)
    .join(', ');
  
  // Validar finalidade como array de strings
  const finalidade = Array.isArray(property.finalidade) 
    ? property.finalidade.filter(f => typeof f === 'string')
    : [];
  const isForSale = finalidade.includes('venda');
  const isForRent = finalidade.includes('locacao');
  const purposeText = isForSale ? 'à venda' : 'para alugar';
  
  const title = `${propertyType} ${purposeText} em ${location}`;
  
  // Descrição otimizada (max 160 caracteres)
  const features = [];
  if (property.quartos) features.push(`${property.quartos} quartos`);
  if (property.suites) features.push(`${property.suites} suítes`);
  if (property.vagas) features.push(`${property.vagas} vagas`);
  if (property.area) features.push(`${property.area}m²`);
  
  const price = isForSale
    ? `R$ ${property.valor_venda?.toLocaleString('pt-BR')}`
    : `R$ ${property.valor_locacao?.toLocaleString('pt-BR')}/mês`;
  
  const description = `${propertyType} ${purposeText} em ${location}. ${features.join(', ')}. ${price}. ${property.descricao?.substring(0, 100) || ''}`.substring(0, 160);
  
  // Keywords otimizadas (garantir que todos valores sejam strings)
  const keywords = [
    propertyType,
    ...finalidade,
    property.endereco_cidade,
    property.endereco_bairro,
    property.endereco_uf,
    'imóvel',
    'imobiliária',
  ]
    .filter(item => item && typeof item === 'string') // Validação extra
    .map(k => String(k).trim()) // Trim para remover espaços
    .filter(k => k.length > 0) // Remove strings vazias
    .join(', ');
  
  // Primeira imagem disponível (suporta todos os formatos + marca d'água)
  const image = property.fotos?.[0] 
    ? getFotoUrl(property.fotos[0], property.marca_dagua || false)
    : undefined;
  
  // URL do imóvel
  const url = `${window.location.origin}/imovel/${property.codigo}`;
  
  // Preço para meta tags
  const priceValue = isForSale
    ? property.valor_venda?.toString()
    : property.valor_locacao?.toString();
  
  // Schema.org JSON-LD para rich snippets
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": title,
    "description": description,
    "image": getFotosUrls(property.fotos, property.marca_dagua || false),
    "offers": {
      "@type": "Offer",
      "url": url,
      "priceCurrency": "BRL",
      "price": priceValue,
      "availability": property.status === 'disponivel' ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": property.endereco_logradouro,
      "addressLocality": property.endereco_cidade,
      "addressRegion": property.endereco_uf,
      "postalCode": property.endereco_cep,
      "addressCountry": "BR"
    },
    "geo": property.latitude && property.longitude ? {
      "@type": "GeoCoordinates",
      "latitude": property.latitude,
      "longitude": property.longitude
    } : undefined,
  };

  // Schema.org para RealEstateAgent (imobiliária)
  const agentSchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": siteName,
    "url": window.location.origin,
  };

  return (
    <>
      <SEO
        title={title}
        description={description}
        keywords={keywords}
        image={image}
        url={url}
        type="product"
        siteName={siteName}
        price={priceValue || undefined}
        currency="BRL"
        availability={property.status === 'disponivel' ? 'InStock' : 'OutOfStock'}
        publishedTime={property.created_at || undefined}
        modifiedTime={property.updated_at || undefined}
      />
    </>
  );
}
